import {
    AGENT_CLIENTS,
    AgentClientType,
    IAgentClientsFilters,
} from '../../../../store/users/usersApi';

const SearchIcon = () => (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
        <path
            d='M21 21l-4.35-4.35M10.8 18a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z'
            stroke='currentColor'
            strokeWidth='1.8'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
);

const toAgentLabel = (agent: string) =>
    agent ? `${agent.charAt(0).toUpperCase()}${agent.slice(1)}` : '';

const AgentClientsFilters = ({
    totalElements,
    filters,
    loading,
    onChange,
    onApply,
    onClear,
}: {
    totalElements: number;
    filters: IAgentClientsFilters;
    loading?: boolean;
    onChange: (next: IAgentClientsFilters) => void;
    onApply: () => void;
    onClear: () => void;
}) => {
    const onTextFilterChange = (key: keyof IAgentClientsFilters, value: string) => {
        onChange({
            ...filters,
            [key]: value,
        });
    };

    const onEnabledChange = (value: string) => {
        const nextFilters = { ...filters };
        if (value === '') {
            delete nextFilters.enabled;
        } else {
            nextFilters.enabled = value === 'true';
        }
        onChange(nextFilters);
    };

    return (
        <div className='rounded-xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black px-3 py-3 my-3'>
            <div className='flex flex-col gap-1 md:flex-row md:items-center md:justify-between'>
                <h4 className='text-sm font-semibold dark:text-primary-darkMode'>
                    Showing {totalElements} agent clients
                </h4>
            </div>

            <div className='mt-3 flex flex-col gap-2 xl:flex-row xl:flex-wrap xl:items-end'>
                <div className='relative w-full xl:max-w-[380px]'>
                    <span className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400'>
                        <SearchIcon />
                    </span>
                    <input
                        value={filters.search || ''}
                        onChange={(e) => onTextFilterChange('search', e.target.value)}
                        placeholder='Search company, email, username or id'
                        className='w-full rounded-xl border bg-white dark:bg-black pl-10 pr-3 py-2 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-gray-300 dark:focus:border-gray-600'
                    />
                </div>

                <div className='relative w-full xl:w-[220px]'>
                    <label className='mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-300'>
                        Agent
                    </label>
                    <select
                        value={filters.agent || ''}
                        onChange={(e) => onTextFilterChange('agent', e.target.value as AgentClientType | '')}
                        className='w-full rounded-xl border bg-white dark:bg-black px-3 py-2 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode focus:outline-none focus:border-gray-300 dark:focus:border-gray-600'
                    >
                        <option value=''>All agents</option>
                        {AGENT_CLIENTS.map((agent) => (
                            <option key={agent} value={agent}>
                                {toAgentLabel(agent)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className='relative w-full xl:w-[220px]'>
                    <label className='mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-300'>
                        Status
                    </label>
                    <select
                        value={typeof filters.enabled === 'boolean' ? String(filters.enabled) : ''}
                        onChange={(e) => onEnabledChange(e.target.value)}
                        className='w-full rounded-xl border bg-white dark:bg-black px-3 py-2 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode focus:outline-none focus:border-gray-300 dark:focus:border-gray-600'
                    >
                        <option value=''>All statuses</option>
                        <option value='true'>Enabled</option>
                        <option value='false'>Disabled</option>
                    </select>
                </div>

                <div className='flex flex-col gap-2 sm:flex-row'>
                    <button
                        type='button'
                        onClick={onApply}
                        disabled={loading}
                        className='rounded-xl border border-[#00330f] bg-[#00330f] px-4 py-2 text-sm font-semibold text-white dark:border-white dark:bg-white dark:text-black hover:opacity-90 disabled:opacity-50'
                    >
                        {loading ? 'Loading...' : 'Apply filters'}
                    </button>
                    <button
                        type='button'
                        onClick={onClear}
                        disabled={loading}
                        className='rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-borderColor-darkMode dark:text-primary-darkMode hover:bg-gray-50 dark:hover:bg-bgColor-darkMode disabled:opacity-50'
                    >
                        Clear
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AgentClientsFilters;
